<template>
    <div class="container-fluid mt-5">
        <div class="d-flex justify-content-end">
            <MaterialButton variant="text" color="info" @click="viewPersons">
                View Persons
            </MaterialButton>
        </div>
        <form @submit.prevent="saveWorkgroup">
            <!-- ID and Workgroup Name Row -->
            <div class="row">
                <div class="col-md-6 mb-3">
                    <material-input id="workgroup-edit-id" label="ID" v-model="workgroupData.id" disabled />
                </div>
                <div class="col-md-6 mb-3">
                    <material-input id="workgroup-edit-name" label="Workgroup Name" v-model="workgroupData.name"
                        placeholder="Enter workgroup name" required />
                </div>
            </div>

            <!-- Description -->
            <div class="row">
                <div class="col-md-6 mb-3">
                    <MaterialTextarea label="Description" id="description" v-model="workgroupData.description" :rows="2"
                        placeholder="Enter description">
                    </MaterialTextarea>
                </div>
            </div>

            <!-- Activity and Status Row -->
            <div class="row">
                <div class="col-md-6 mb-3">
                    <material-select label="Activity" v-model="workgroupData.activity" :options="activityOptions"
                        :disabled="isLoadingActivities" :loading="isLoadingActivities" />
                </div>
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Status</label>
                        <HelpTextTooltip helpKey="workgroup_detail_status" />
                    </div>
                    <material-select v-model="workgroupData.status" :options="statusOptions" />
                </div>
            </div>

            <!-- Skill and Person Count Row -->
            <div class="row">
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-end gap-2">
                        <div class="flex-grow-1">
                            <div class="d-flex align-items-center gap-2 mb-2">
                                <label class="form-label mb-0">Skill</label>
                                <HelpTextTooltip helpKey="workgroup_detail_skill" />
                            </div>
                            <material-select v-model="workgroupData.skill" :options="skillOptions"
                                :disabled="isLoadingSkills" :loading="isLoadingSkills" />
                        </div>
                    </div>
                </div>
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Person Count</label>
                        <HelpTextTooltip helpKey="workgroup_detail_personsneeded" />
                    </div>
                    <material-input id="workgroup-edit-person-count"
                        v-model="workgroupData.personCount" type="number" min="1" />
                </div>
            </div>

            <!-- SMS Options Row -->
            <div class="row">
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Send SMS</label>
                        <HelpTextTooltip helpKey="workgroup_detail_wgsms" />
                    </div>
                    <material-select v-model="workgroupData.sendSms" :options="yesNoOptions" />
                </div>
            </div>

            <label v-if="workgroupData.sendSms">*SMS alerts will be sent {{ workgroupData.smsRangeDays }} days before the Instance date (Location
                setting)</label>

            <div v-if="workgroupData.sendSms" class="row">
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Send Secondary SMS</label>
                        <HelpTextTooltip helpKey="workgroup_detail_2ndwgsms" />
                    </div>
                    <material-select v-model="workgroupData.sendSecondarySms"
                        :options="yesNoOptions" />
                </div>
                <div v-if="workgroupData.sendSecondarySms" class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Send a secondary SMS reminder (days) before the Instance date</label>
                        <HelpTextTooltip helpKey="workgroup_detail_2ndwgsmsdays" />
                    </div>
                    <material-input id="workgroup-edit-secondary-sms-days" type="number"
                        v-model="workgroupData.sendSecondarySmsDaysBeforeInstanceDate" />
                </div>
            </div>

            <!-- SMS Reminder Type -->
            <div class="row">
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">SMS Reminder Type</label>
                        <HelpTextTooltip helpKey="workgroup_detail_remindertype" />
                    </div>
                    <material-select v-model="workgroupData.smsReminderType"
                        :options="smsReminderOptions" />
                </div>
            </div>

            <!-- Sign Up Only -->
            <div class="row">
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Manual Sign-up / Sign Up Only</label>
                        <HelpTextTooltip helpKey="workgroup_detail_signuponly" />
                    </div>
                    <material-select v-model="workgroupData.signUpOnly"
                        :options="enabledDisabledOptions" />
                </div>
                <div v-if="workgroupData.signUpOnly" class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Sign Up Allow Multiple Assignments</label>
                        <HelpTextTooltip helpKey="workgroup_detail_signuponly_allow_multiple" />
                    </div>
                    <material-select v-model="workgroupData.signupOnlyAllowMultiple"
                        :options="enabledDisabledOptions" />
                </div>
            </div>

            <!-- Advertise Sign up -->
            <div class="row">
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Advertise Sign up</label>
                        <HelpTextTooltip helpKey="workgroup_detail_advertisesignup" />
                    </div>
                    <material-select v-model="workgroupData.advertiseSignUp"
                        :options="enabledDisabledOptions" />
                </div>
            </div>

            <!-- Publicly Viewable and Auto Schedule Row -->
            <div class="row">
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Publicly Viewable</label>
                        <HelpTextTooltip helpKey="workgroup_detail_publicview" />
                    </div>
                    <material-select v-model="workgroupData.publiclyViewable"
                        :options="enabledDisabledOptions" />
                </div>
                <!-- Auto Schedule (hidden when Sign Up Only is enabled) -->
                <div v-if="!workgroupData.signUpOnly" class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Auto Schedule</label>
                        <HelpTextTooltip helpKey="workgroup_detail_autosched" />
                    </div>
                    <material-select v-model="workgroupData.autoSchedule"
                        :options="enabledDisabledOptions" />
                </div>
            </div>

            <!-- Auto Substitute and Convert to Sign-up Row (only when Auto Schedule is enabled) -->
            <div v-if="workgroupData.autoSchedule" class="row">
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Auto Substitute</label>
                        <HelpTextTooltip helpKey="workgroup_detail_autosub" />
                    </div>
                    <material-select v-model="workgroupData.autoSubstitute"
                        :options="enabledDisabledOptions" />
                </div>
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Convert to Sign-up</label>
                        <HelpTextTooltip helpKey="workgroup_detail_converttosignup" />
                    </div>
                    <material-select v-model="workgroupData.convertToSignUp"
                        :options="enabledDisabledOptions" />
                </div>
            </div>

            <!-- Enable Swap and Allow Overlap Row -->
            <div class="row">
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Enable Swap</label>
                        <HelpTextTooltip helpKey="workgroup_detail_enableswap" />
                    </div>
                    <material-select v-model="workgroupData.enableSwap" :options="yesNoOptions" />
                </div>
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Allow Overlap</label>
                        <HelpTextTooltip helpKey="workgroup_detail_allowoverlap" />
                    </div>
                    <material-select v-model="workgroupData.allowOverlap"
                        :options="yesNoOptions" />
                </div>
            </div>

            <!-- Notification Options Row -->
            <div class="row">
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Auto-Scheduler Completion Notification</label>
                        <HelpTextTooltip helpKey="workgroup_detail_autocomplete" />
                    </div>
                    <material-select v-model="workgroupData.autoSchedulerNotification" :options="yesNoOptions" />
                </div>
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Auto-Substitute/Swap Notification</label>
                        <HelpTextTooltip helpKey="workgroup_detail_autosubstitute_adminalert" />
                    </div>
                    <material-select v-model="workgroupData.autoSubstituteNotification" :options="yesNoOptions" />
                </div>
            </div>

            <!-- Send Admin Report and Ignore Preferences Row -->
            <div class="row">
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Send Admin Report</label>
                        <HelpTextTooltip helpKey="workgroup_detail_send_admin_report" />
                    </div>
                    <material-select v-model="workgroupData.sendAdminReport" :options="yesNoOptions" />
                </div>
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Ignore Preferences</label>
                        <HelpTextTooltip helpKey="workgroup_detail_ignore_preferences" />
                    </div>
                    <material-select v-model="workgroupData.ignorePreferences" :options="yesNoOptions" />
                </div>
            </div>

            <!-- Priority and Decline Days Row -->
            <div class="row">
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Priority (1=low, 100=high)</label>
                        <HelpTextTooltip helpKey="workgroup_detail_priority" />
                    </div>
                    <material-input id="workgroup-edit-priority"
                        v-model="workgroupData.priority" type="number" min="1" max="100" />
                </div>
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Decline days (before activity date)</label>
                        <HelpTextTooltip helpKey="workgroup_detail_declinedays" />
                    </div>
                    <material-input id="workgroup-edit-decline-days"
                        v-model="workgroupData.declineDays" type="number" min="0" />
                </div>
            </div>

            <!-- Auto Decline and Schedule by Teams Row -->
            <div class="row">
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Auto Decline Assignments</label>
                        <HelpTextTooltip helpKey="workgroup_detail_autodecline" />
                    </div>
                    <material-select v-model="workgroupData.autoDeclineAssignments"
                        :options="enabledDisabledOptions" />
                </div>
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Schedule by Teams (in sequential order)</label>
                        <HelpTextTooltip helpKey="workgroup_detail_teams" />
                    </div>
                    <material-select v-model="workgroupData.scheduleByTeams" :options="enabledDisabledOptions" />
                </div>
            </div>

            <!-- Team Settings (shown when Schedule by Teams is enabled) -->
            <div v-if="workgroupData.scheduleByTeams" class="row">
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Next Team (to be scheduled)</label>
                        <HelpTextTooltip helpKey="workgroup_detail_nextteam" />
                    </div>
                    <material-select v-model="workgroupData.nextTeam" :options="teamOptions" />
                </div>
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Rotate Teams by</label>
                        <HelpTextTooltip helpKey="workgroup_detail_teamrotatemethod" />
                    </div>
                    <material-select v-model="workgroupData.teamRotateMethod" :options="teamRotateOptions" />
                </div>
            </div>
            <div v-if="workgroupData.scheduleByTeams" class="row">
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Skip Team if unavailable</label>
                        <HelpTextTooltip helpKey="workgroup_detail_skipteam" />
                    </div>
                    <material-select v-model="workgroupData.skipTeamIfUnavailable" :options="yesNoOptions" />
                </div>
            </div>

            <!-- Auto Decline Settings (shown when Auto Decline is enabled) -->
            <div v-if="workgroupData.autoDeclineAssignments" class="row mb-3">
                <div class="col-12">
                    <div class="border rounded p-3 bg-light">
                        <div class="d-flex flex-wrap align-items-center gap-2">
                            <span class="text-sm">Automatically decline assignments within</span>
                            <material-input id="workgroup-autodecline-days-assignment" type="number"
                                v-model="workgroupData.autoDeclineDaysAssignment"
                                :min="1" :max="autoDeclineMax.daysAssignment"
                                style="width: 80px; display: inline-block;" />
                            <span class="text-sm">days of the assignment/Instance date if the assigned user has not responded</span>
                            <material-input id="workgroup-autodecline-days-reminder" type="number"
                                v-model="workgroupData.autoDeclineDaysReminder"
                                :min="1" :max="autoDeclineMax.daysReminder"
                                style="width: 80px; display: inline-block;" />
                            <span class="text-sm">days after the most recent reminder.</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Admin Reply-to Email -->
            <div class="mb-3">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <label class="form-label mb-0">Admin reply-to Email</label>
                    <HelpTextTooltip helpKey="workgroup_detail_adminemail" />
                </div>
                <material-input id="workgroup-edit-admin-email"
                    v-model="workgroupData.adminEmail" type="email" placeholder="email@location.com" />
            </div>

            <!-- iCal URL -->
            <div class="mb-4">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <label class="form-label mb-0">iCal URL</label>
                    <HelpTextTooltip helpKey="workgroup_detail_icalurl" />
                </div>
                <material-input id="workgroup-edit-ical-url" v-model="workgroupData.iCalUrl" type="url"
                    placeholder="http://example.com/ical" disabled>
                    <template #append>
                        <button
                            class="btn btn-sm border border-1 bg-transparent px-2 m-0 d-flex align-items-center justify-content-center"
                            type="button" @click="copyIcalUrl" :disabled="!workgroupData.iCalUrl" title="Copy iCal URL">
                            <i class="material-icons text-sm">content_copy</i>
                        </button>
                    </template>
                </material-input>
            </div>

            <!-- Action Buttons -->
            <div class="d-flex justify-content-end gap-2">
                <material-button @click="saveWorkgroup" variant="primary" :class="['px-4', hasChanges && !isSaving ? 'pulse-glow' : '']" :disabled="isSaving">
                    <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status"
                        aria-hidden="true"></span>
                    <i v-else class="material-icons text-lg me-1">save</i>
                    {{ isSaving ? 'Saving...' : 'Save' }}
                </material-button>
            </div>
        </form>

        <!-- Persons Modal -->
        <ContentModal v-model="showPersonsModal" title="Persons with Selected Skill" size="xl">
            <BaseTable :rows="personsWithSkill" :columns="skillPersonsColumns" :selectable="false" :searchable="true"
                :loading="personsLoading" :pagination="false" />
        </ContentModal>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import MaterialInput from '@/components/common/MaterialInput.vue'
import MaterialSelect from '@/components/common/MaterialSelect.vue'
import MaterialButton from '@/components/common/MaterialButton.vue'
import MaterialTextarea from '@/components/common/MaterialTextarea.vue'
import BaseTable from '@/components/Table/Table.vue'
import ContentModal from '../../../components/Modal/ContentModal.vue'
import HelpTextTooltip from '@/components/common/HelpTextTooltip.vue'
import { getLocationId } from '@/utils/SessionDataHandler'
import { useToast } from 'vue-toast-notification'
import { toGetcalUrl } from '@/utils/IcalUrlHelper'

const store = useStore()
const route = useRoute()
const locationId = getLocationId()
const workgroupId = Number(route.params.workgroupid)
const toast = useToast()

const workgroupData = reactive({
    id: null,
    name: '',
    description: '',
    activity: null,
    status: 'active',
    skill: null,
    personCount: 1,
    sendSms: true,
    smsRangeDays: 2,
    sendSecondarySmsDaysBeforeInstanceDate: 0,
    sendSecondarySms: false,
    smsReminderType: 1,
    signUpOnly: false,
    signupOnlyAllowMultiple: false,
    advertiseSignUp: false,
    publiclyViewable: true,
    autoSchedule: true,
    autoSubstitute: true,
    convertToSignUp: false,
    enableSwap: true,
    allowOverlap: false,
    autoSchedulerNotification: false,
    autoSubstituteNotification: true,
    priority: 5,
    declineDays: 1,
    autoDeclineAssignments: false,
    autoDeclineDaysAssignment: 6,
    autoDeclineDaysReminder: 2,
    scheduleByTeams: true,
    nextTeam: null,
    teamRotateMethod: 'week',
    skipTeamIfUnavailable: false,
    teams: [],
    adminEmail: '',
    iCalUrl: '',
})

const autoDeclineMax = reactive({
    daysAssignment: 12,
    daysReminder: 6,
})

const yesNoOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
]

const enabledDisabledOptions = [
    { label: 'Enabled', value: true },
    { label: 'Disabled', value: false },
]

const statusOptions = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
]

// Activity options - populated from API
const activityOptions = computed(() => {
    const activities = store.getters.activitiesByLocation
    return activities
        .map(activity => ({
            label: activity.activityname,
            value: activity.activityid
        }))
})

const skillOptions = computed(() => {
    const skills = store.getters.skillsByLocation || []
    return skills
        .map(skill => ({
            label: skill.skillname,
            value: skill.skillid
        }))
})

const smsReminderOptions = [
    { label: 'Only send sms/text reminders when the assignment status is PENDING or has not yet been confirmed', value: 1 },
    { label: 'Send SMS for all assignments', value: 2 },
    { label: 'Never send SMS', value: 3 },
]

const teamRotateOptions = [
    { label: 'Day', value: 'day' },
    { label: 'Week', value: 'week' },
    { label: 'Month', value: 'month' },
    { label: 'Year', value: 'year' },
]

const teamOptions = computed(() => {
    return (workgroupData.teams || []).map(team => ({
        label: team.name,
        value: team.id,
    }))
})

const isSaving = ref(false)
const baseline = ref(null)
const isLoadingActivities = computed(() => store.getters.workgroupsLoading)
const isLoadingSkills = computed(() => store.getters.workgroupsLoading)
const workgroupDetails = computed(() => store.getters.workgroupDetails)

const hasChanges = computed(() => {
    if (!baseline.value) return false
    return JSON.stringify(baseline.value) !== JSON.stringify({ ...workgroupData })
})

// Persons modal state
const showPersonsModal = ref(false)
const personsLoading = ref(false)
const personsWithSkill = computed(() => {
    const skillPersons = store.getters.skillPersonsForWorkgroup || []
    return skillPersons.map((person) => ({
        personid: person.personid,
        fullname: `${person.firstname} ${person.lastname}`,
        email: person.email,
        skilllevel: person.skilllevel || '-',
    }))
})

const skillPersonsColumns = [
    { key: "personid", label: "ID" },
    { key: "fullname", label: "Name" },
    { key: "email", label: "Email" },
    { key: "skilllevel", label: "Skill Level" },
]

watch(workgroupDetails, (details) => {
    if (details) {
        Object.assign(workgroupData, {
            id: details.id ?? workgroupData.id,
            name: details.workgroup_name ?? workgroupData.name,
            description: details.description ?? workgroupData.description,
            activity: details.activity?.id ?? workgroupData.activity,
            status: details.status ?? workgroupData.status,
            skill: details.skill?.id ?? workgroupData.skill,
            personCount: details.person_count ?? workgroupData.personCount,
            sendSms: details.send_sms ?? workgroupData.sendSms,
            smsRangeDays: details.sms_range_days ?? workgroupData.smsRangeDays,
            sendSecondarySms: details.send_secondary_sms ?? workgroupData.sendSecondarySms,
            sendSecondarySmsDaysBeforeInstanceDate: details.send_secondary_sms_days_before_instance_date ?? workgroupData.sendSecondarySmsDaysBeforeInstanceDate,
            smsReminderType: details.sms_reminder_type ?? workgroupData.smsReminderType,
            signUpOnly: details.sign_up_only ?? workgroupData.signUpOnly,
            signupOnlyAllowMultiple: details.signuponly_allow_multiple ?? workgroupData.signupOnlyAllowMultiple,
            advertiseSignUp: details.advertise_sign_up ?? workgroupData.advertiseSignUp,
            publiclyViewable: details.publicly_viewable ?? workgroupData.publiclyViewable,
            autoSchedule: details.auto_schedule ?? workgroupData.autoSchedule,
            autoSubstitute: details.auto_substitute ?? workgroupData.autoSubstitute,
            convertToSignUp: details.convert_to_signup ?? workgroupData.convertToSignUp,
            enableSwap: details.enable_swap ?? workgroupData.enableSwap,
            allowOverlap: details.allow_overlap ?? workgroupData.allowOverlap,
            autoSchedulerNotification: details.auto_scheduler_completion_notification ?? workgroupData.autoSchedulerNotification,
            autoSubstituteNotification: details.auto_substitute_swap_notification ?? workgroupData.autoSubstituteNotification,
            sendAdminReport: details.send_admin_report ?? workgroupData.sendAdminReport,
            ignorePreferences: details.ignore_preferences ?? workgroupData.ignorePreferences,
            priority: details.priority ?? workgroupData.priority,
            declineDays: details.decline_days_before_activity_date ?? workgroupData.declineDays,
            autoDeclineAssignments: details.auto_decline_assignments ?? workgroupData.autoDeclineAssignments,
            autoDeclineDaysAssignment: details.auto_decline_days_assignment ?? details.auto_decline_defaults?.days_assignment ?? workgroupData.autoDeclineDaysAssignment,
            autoDeclineDaysReminder: details.auto_decline_days_reminder ?? details.auto_decline_defaults?.days_reminder ?? workgroupData.autoDeclineDaysReminder,
            scheduleByTeams: details.schedule_by_teams_in_sequential_order ?? workgroupData.scheduleByTeams,
            nextTeam: details.next_team_id ?? workgroupData.nextTeam,
            teamRotateMethod: details.team_rotate_method ?? workgroupData.teamRotateMethod,
            skipTeamIfUnavailable: details.skip_team_if_unavailable ?? workgroupData.skipTeamIfUnavailable,
            teams: details.teams ?? workgroupData.teams,
            adminEmail: details.admin_reply_to_email ?? workgroupData.adminEmail,
            iCalUrl: details.ical_url ? toGetcalUrl(details.ical_url) : workgroupData.iCalUrl,
        })
        // Update auto-decline max values from API config
        if (details.auto_decline_max) {
            autoDeclineMax.daysAssignment = details.auto_decline_max.days_assignment ?? 12
            autoDeclineMax.daysReminder = details.auto_decline_max.days_reminder ?? 6
        }
        baseline.value = { ...workgroupData }
    }
}, { immediate: true })

// Fetch activities and workgroup details on mount
onMounted(async () => {
    if (locationId) {
        try {
            // Fetch activities
            await store.dispatch('fetchActivitiesByLocation', {
                locationId,
                all: true
            })

            // Fetch skills
            await store.dispatch('fetchSkillsByLocation', {
                locationId,
                all: true
            })
        } catch (error) {
            console.error('Error fetching activities or skills:', error)
        }
    }

    // Fetch workgroup details by ID
    if (workgroupId) {
        try {
            await store.dispatch('fetchWorkgroupById', workgroupId)
        } catch (error) {
            console.error('Error fetching workgroup details:', error)
        }
    }
})

const saveWorkgroup = async () => {
    isSaving.value = true
    try {
        console.log('Saving workgroup:', workgroupData)
        await store.dispatch('saveWorkgroup', {
            workgroupid: workgroupData.id,
            workgroupname: workgroupData.name,
            description: workgroupData.description,
            activityid: workgroupData.activity,
            status: workgroupData.status,
            workgroupskillid: workgroupData.skill,
            personcount: workgroupData.personCount,
            sendwgsms: workgroupData.sendSms,
            workgroupsendsms: workgroupData.sendSecondarySms,
            workgroupsmsrangedays: workgroupData.sendSecondarySmsDaysBeforeInstanceDate,
            sendremindertype: workgroupData.smsReminderType,
            signuponly: workgroupData.signUpOnly,
            signuponly_allow_multiple: workgroupData.signupOnlyAllowMultiple,
            advertisesignup: workgroupData.advertiseSignUp,
            publicview: workgroupData.publiclyViewable,
            autoschedule: workgroupData.autoSchedule,
            autosubstitute: workgroupData.autoSubstitute,
            converttosignup: workgroupData.convertToSignUp,
            enableswap: workgroupData.enableSwap,
            allowoverlap: workgroupData.allowOverlap,
            autoschedule_complete_adminalert: workgroupData.autoSchedulerNotification,
            autosubstitute_adminalert: workgroupData.autoSubstituteNotification,
            send_admin_report: workgroupData.sendAdminReport,
            ignore_preferences: workgroupData.ignorePreferences,
            priority: workgroupData.priority,
            declinedays: workgroupData.declineDays,
            autodecline: workgroupData.autoDeclineAssignments,
            autodeclinedaysassignment: workgroupData.autoDeclineDaysAssignment,
            autodeclinedaysreminder: workgroupData.autoDeclineDaysReminder,
            enableteams: workgroupData.scheduleByTeams,
            nextteam: workgroupData.nextTeam,
            teamrotatemethod: workgroupData.teamRotateMethod,
            skipteam: workgroupData.skipTeamIfUnavailable,
            adminemail: workgroupData.adminEmail,
        })
        baseline.value = { ...workgroupData }
        toast.success('Workgroup saved successfully!', { position: 'top-right' })
    } catch (error) {
        console.error('Error saving workgroup:', error)
        toast.error(`Failed to save workgroup: ${error.message}`, { position: 'top-right' })
    } finally {
        isSaving.value = false
    }
}

const viewPersons = async () => {
    if (!workgroupData.skill) {
        toast.warning("Please select a skill first!", { position: "top-right" })
        return
    }

    showPersonsModal.value = true
    personsLoading.value = true

    try {
        await store.dispatch("getSkillPersonsForWorkgroup", {
            skillId: workgroupData.skill,
            locationId: locationId,
        })
    } catch (error) {
        console.error("Error fetching skill persons:", error)
        toast.error("Failed to load persons for selected skill.", {
            position: "top-right",
        })
    } finally {
        personsLoading.value = false
    }
}
const copyIcalUrl = async () => {
    if (!workgroupData.iCalUrl) {
        toast.error("No iCal URL available to copy", { position: "top-right" });
        return;
    }

    try {
        await navigator.clipboard.writeText(workgroupData.iCalUrl);
        toast.success("iCal URL copied to clipboard!", { position: "top-right" });
    } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = workgroupData.iCalUrl;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand("copy");
            toast.success("iCal URL copied to clipboard!", { position: "top-right" });
        } catch (fallbackErr) {
            toast.error("Failed to copy iCal URL", { position: "top-right" });
        }
        document.body.removeChild(textArea);
    }
}
</script>

<style scoped>
.form-label {
    color: #7b809a;
}

.d-flex {
    display: flex;
}

.align-items-end {
    align-items: flex-end;
}

.flex-grow-1 {
    flex-grow: 1;
}

.gap-2 {
    gap: 0.5rem;
}

.text-decoration-none {
    text-decoration: none;
}

.text-decoration-none:hover {
    text-decoration: underline;
}
</style>